import { useCallback, useEffect, useRef } from 'react'
import { copilotBaseUrl } from '../api/baseUrl'
import { postInterruptKeepalive } from '../api/copilotApi'
import { SessionState, TERMINAL_PHASES } from '../state/types'

const isLive = (s: SessionState): boolean => !TERMINAL_PHASES.includes(s.phase)

/**
 * Warns the operator before they leave with a run in flight, and stops any run they abandon.
 *
 * Takes `state.sessions` rather than the whole `CopilotState`: the guard reads `phase` and
 * `sessionId` and nothing else, so `open`/`activeId`/`order` are none of its business.
 *
 * Returns the sweep so `CopilotProvider` can run it from its own unmount cleanup, which covers the
 * teardown paths `pagehide` never sees — a client-side unmount with no document navigation, such as
 * `copilotEnabled` flipping. The identity is stable and MUST stay that way: the provider's cleanup
 * fires whenever that effect re-runs, so an unstable sweep would interrupt every live run on every
 * render. `CopilotProvider.test.tsx` pins this with a re-render case.
 */
export function useRunGuard(sessions: Record<string, SessionState>): () => void {
  // The token rides along with the sessions, and is snapshotted rather than read at teardown on
  // purpose: every logout ends in `AuthenticationService.endSession`, which removes `accessToken` and
  // only then calls `window.location.replace`, so by the time `pagehide` fires the token is gone. No render
  // happens between the click and the navigation, so the last value mirrored here is the one that
  // was still valid — which is what makes the sweep work on logout at all.
  const guardRef = useRef({ sessions, token: '' })
  // Written from an effect rather than during render: reading or writing a ref in the render body
  // is not safe under concurrent rendering. Passive effects flush before any user-visible teardown,
  // so both handlers below still see current values.
  useEffect(() => { guardRef.current = { sessions, token: localStorage.getItem('accessToken') ?? '' } })

  const sweep = useCallback(() => {
    const baseUrl = copilotBaseUrl()
    const { sessions: current, token } = guardRef.current
    Object.values(current).forEach((s) => {
      // The predicate is the id, never the phase name: SESSION_CREATED lands while the phase is
      // still `creating`, so a session there may well have a run worth stopping.
      if (isLive(s) && s.sessionId) postInterruptKeepalive(baseUrl, s.sessionId, token)
    })
  }, [])

  const armed = Object.values(sessions).some(isLive)

  // Registered only while there is something to lose — both the current bfcache guidance and the
  // operator's patience depend on not holding this listener when no run is in flight.
  useEffect(() => {
    if (!armed) return undefined
    const warn = (e: BeforeUnloadEvent) => {
      // No custom wording: every modern browser discards it and shows its own string.
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [armed])

  // `pagehide`, not `beforeunload`, is what actually spends the interrupt: `beforeunload` fires
  // before the operator has answered the prompt, so interrupting there would kill the run of
  // someone who chose to stay. Registered once and reading through the ref, because re-registering
  // on every streamed frame just to refresh this closure would thrash for no gain.
  useEffect(() => {
    const onPageHide = (e: PageTransitionEvent) => {
      // Entering bfcache, not leaving: the page may be restored, and its runs must survive.
      if (e.persisted) return
      sweep()
    }
    window.addEventListener('pagehide', onPageHide)
    // Removing the listener is not a teardown event, so this cleanup must NOT sweep — the provider's
    // own cleanup owns that, once.
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [sweep])

  return sweep
}
